import type { DataSourceAbstract, SelectDataSourceVariable, TypeInstanceMap, VariableADTvalue } from '@lighthouse/core'
import { type Field, VariableType } from '@lighthouse/core'
import { Placeholder } from '@tiptap/extensions'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import type { JSONContent } from '@tiptap/react'
import { EditorContent, Extension, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useDebounce, useLatest } from 'react-use'
import styled, { css } from 'styled-components'
import { useImmer } from 'use-immer'

import { numberReg } from '../../constants/reg'
import { WorkflowNodeFieldFormulaContext } from '../../contexts/WorkflowNodeFieldFormulaContext'
import { getInputInnerTypeMatch } from '../../utils'
import type { VariableDropDownOption } from '../'
import { getDateVariableOption, VariableBlock, VariableSelect } from '../'
import SelectDataSourceModal from '../Variable/VariableSelect/SelectDataSource'
import { DATE_SYSTEM, VariablePicker } from '../VariablePicker'
import { AddVariableButton } from './AddVariableButton'
import { NodeFieldEditorProvider } from './EditorProvider'

const SCxContainer = styled.div<{ highlighting?: boolean }>`
    position: relative;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: space-between;
    border-radius: 6px;
    /* border: 1px solid var(--color-gray-200); */
    border: 1px solid transparent;
    background-color: var(--color-gray-100);

    ${({ highlighting }) =>
        highlighting &&
        css`
            border-color: var(--color-main);
            box-shadow: 0 0 4px 0 var(--color-main);
        `}

    &:focus-within {
        background-color: white;
        border: 1px solid var(--color-main);
    }

    & .node-editor {
        display: flex;
        width: 100%;
        padding: 4px 0 4px 8px;
        margin-right: 8px;
        line-height: 24px;
        height: 100%;
        overflow: hidden;
        word-break: break-all;
    }

    & p {
        font-size: var(--font-size-normal);
        /* white-space: nowrap; */
    }

    & .ProseMirror {
        width: 100%;
        /* flex: 1; */
        /* white-space: nowrap; */
        /* align-self: stretch; */
    }

    & .ProseMirror p.is-editor-empty:first-child::before {
        color: #adb5bd;
        content: attr(data-placeholder);
        float: left;
        height: 0;
        pointer-events: none;
    }
`

const SCxExtra = styled.div<{ width?: number }>`
    width: 30px;
    margin-top: 6px;
`

// const singleVariableFieldTypes = new Set(['select', 'checkbox', 'person', 'date', 'number', 'role', 'department', 'singleSelect'])

export interface NodeFieldPreviewProps extends Omit<VariableDropDownOption, 'dateOption'> {
    style?: React.CSSProperties
    field?: Field
    placeholder?: string
    autoHeight?: boolean
    highlighting?: boolean
    disabled?: boolean
    value?: JSONContent
    enableSelectDataSource?: boolean
    dataSourceList?: DataSourceAbstract[]
    onChange?: (value: JSONContent) => void
    onBlur?: (value: JSONContent) => void
}

interface NodeFieldPreviewState {
    modalOpened: boolean
    selectDataSourceVariable?: SelectDataSourceVariable
}

export const NodeFieldPreview: React.FC<NodeFieldPreviewProps> = ({
    innerTypes: propsInnerTypes,
    pageOptions,
    upstreamOptions,
    datasourceOption,
    userOption,
    viewOptions,
    formOption,
    eventOption,
    loopOption,
    inputOption,
    filterOption,
    imageOption,
    style,
    value,
    field,
    placeholder = '请输入',
    autoHeight = false,
    highlighting,
    disabled,
    dataSourceList,
    enableSelectDataSource,
    disabledFormula,
    onChange,
    onBlur
}) => {
    const workflowDisablesFormula = useContext(WorkflowNodeFieldFormulaContext)
    const [editorValue, setEditorValue] = useState(value)
    const [state, setState] = useImmer<NodeFieldPreviewState>({
        modalOpened: false,
        selectDataSourceVariable: undefined
    })
    const { modalOpened, selectDataSourceVariable } = state
    const fieldInnerType = field?.innerType ?? 'TEXT'
    const effectiveDisabledFormula = Boolean(disabledFormula) || workflowDisablesFormula === true || fieldInnerType === 'RICH_TEXT'
    const innerTypes = useMemo(() => {
        if (propsInnerTypes) {
            return propsInnerTypes
        }
        const matched = getInputInnerTypeMatch(fieldInnerType)
        return Array.isArray(matched) ? matched : matched ? [matched] : []
    }, [fieldInnerType, propsInnerTypes])
    const notEditable = fieldInnerType !== 'TEXT' && field?.type !== 'file'
    const dateOption = useMemo(() => {
        if (fieldInnerType === 'DATE') {
            return getDateVariableOption([...DATE_SYSTEM, 'NOW'])
        }
        return undefined
    }, [fieldInnerType])

    const latestRef = useLatest({ onChange, onBlur })

    const isCompositionInputRef = useRef(false)
    const selectDataSourceProps = useMemo(() => {
        if (!enableSelectDataSource) {
            return
        }
        return {
            inputOption,
            viewOptions,
            upstreamOptions,
            userOption,
            pageOptions,
            validateFieldType: (f: Field) => f.innerType === fieldInnerType
        }
    }, [enableSelectDataSource, fieldInnerType, inputOption, pageOptions, upstreamOptions, userOption, viewOptions])

    // const handleChangeVariableValue = useCallback((value: VariableADTvalue) => {
    //     // latestRef.current.onChange?.(value)
    // }, [latestRef])

    const handleOpenSelectDataSource = useCallback(
        (val: SelectDataSourceVariable) => {
            setState({
                modalOpened: true,
                selectDataSourceVariable: val
            })
        },
        [setState]
    )

    const extensions = useMemo(
        () => [
            Extension.create({
                addKeyboardShortcuts() {
                    return {
                        Enter: e => {
                            return true
                        }
                    }
                },
                addProseMirrorPlugins() {
                    return [
                        new Plugin({
                            key: new PluginKey('eventHandler'),
                            // this: P, view: EditorView, from: number, to: number, text: string
                            props: {
                                handleTextInput(view, from, to, text) {
                                    if (fieldInnerType === 'NUMBER') {
                                        return !numberReg.test(text)
                                    }
                                }
                                // … and many, many more.
                                // Here is the full list: https://prosemirror.net/docs/ref/#view.EditorProps
                            }
                        })
                    ]
                }
            }),

            StarterKit.configure({
                // history: false,
                heading: false,
                bulletList: false,
                orderedList: false,
                blockquote: false,
                codeBlock: false,
                horizontalRule: false
            }),
            Placeholder.configure({
                showOnlyWhenEditable: false,
                showOnlyCurrent: false,
                placeholder
            }),
            VariableBlock.configure({
                pageOptions,
                upstreamOptions,
                userOption,
                viewOptions,
                formOption,
                eventOption,
                inputOption,
                filterOption,
                imageOption,
                datasourceOption,
                dataSources: dataSourceList,
                onOpenSelectDataSourceModal: handleOpenSelectDataSource
                // onChangeVariableValue: handleChangeVariableValue
            })
        ],
        [
            placeholder,
            pageOptions,
            upstreamOptions,
            userOption,
            viewOptions,
            formOption,
            eventOption,
            inputOption,
            filterOption,
            imageOption,
            datasourceOption,
            dataSourceList,
            handleOpenSelectDataSource,
            fieldInnerType
        ]
    )

    const getMatchFieldValueOrErrorVariable = useCallback((field: Field | undefined, content: JSONContent[] | undefined) => {
        if (!content) {
            return undefined
        }
        const firstValue = content?.[0]
        if (!firstValue) {
            return undefined
        }
        const isMatchFieldValue = content && content.length > 1
        if (isMatchFieldValue || firstValue.type === 'text') {
            return {
                type: VariableType.VARIABLE,
                fieldVariable: {
                    dsId: '',
                    fieldId: ''
                }
            }
        }
        if (firstValue.type === 'variable') {
            if (firstValue.attrs?.value?.type === VariableType.VALUE) {
                if (firstValue.attrs?.value?.valueVariable?.type === field?.type || !firstValue.attrs?.value?.valueVariable) {
                    return firstValue?.attrs?.value
                }
                return {
                    type: VariableType.VARIABLE,
                    fieldVariable: {
                        dsId: '',
                        fieldId: ''
                    }
                }
            }
            return firstValue?.attrs?.value
        }
    }, [])

    const editor = useEditor(
        {
            // autofocus: false,
            // enableInputRules: true,
            // enablePasteRules: true,
            content: editorValue,
            editable: !(disabled ?? notEditable),
            // injectCSS: false,
            // enableInputRules: [StarterKit, 'number'],
            extensions,
            editorProps: {
                handleDOMEvents: {
                    compositionstart: () => {
                        isCompositionInputRef.current = true
                    },
                    compositionend: view => {
                        isCompositionInputRef.current = false
                        latestRef.current.onChange?.(view.state.doc.toJSON())
                    }
                }
            },
            onUpdate({ editor, transaction }) {
                if (isCompositionInputRef.current) {
                    return
                }
                const json = editor.getJSON()
                setEditorValue(json)
                latestRef.current.onChange?.(json)
                // When editor is not focused (e.g., updateAttributes called from a modal/popover),
                // onBlur won't fire naturally, so we trigger it here to ensure callers that rely
                // solely on onBlur (not onChange) still receive the updated content.
                if (!editor.isFocused) {
                    latestRef.current.onBlur?.(json)
                }
            },
            onBlur({ editor, transaction }) {
                const json = editor.getJSON()
                latestRef.current.onBlur?.(json)
            }
        },
        [latestRef]
    )

    useDebounce(
        () => {
            if (editor && !editor.isDestroyed && !editor.isFocused) {
                setEditorValue(value)
                if (value) {
                    editor
                        .chain()
                        .setContent(value, { emitUpdate: false })
                        .setTextSelection({ from: editor.state.selection.from, to: editor.state.selection.to })
                        .run()
                } else {
                    editor.commands.clearContent(false)
                }
            }
        },
        30,
        [value]
    )

    // 解决找不到变量的问题，当新加一个变量时，这是options更新了，但是tiptap内部的variable component 没有更新
    useEffect(() => {
        editor?.setOptions({
            extensions
        })
    }, [editor, extensions])

    const handleOpenSelectDataSourceModal = useCallback(() => {
        setState({ modalOpened: true })
    }, [setState])

    const handleCloseSelectDataSourceModal = useCallback(() => {
        setState({ modalOpened: false })
    }, [setState])

    const handleEditorSelect = useCallback(
        (value: VariableADTvalue) => {
            if (!editor) {
                return
            }
            const content = {
                type: 'variable',
                attrs: { value }
            }

            editor.chain().focus().insertContent([content]).run()
        },
        [editor]
    )

    const handleSelect = useCallback(
        (value: VariableADTvalue) => {
            const val = {
                type: 'doc',
                content: [
                    {
                        type: 'paragraph',
                        content: [
                            {
                                type: 'variable',
                                attrs: {
                                    value
                                }
                            }
                        ]
                    }
                ]
            }
            if (latestRef.current.onChange) {
                latestRef.current.onChange?.(val)
                return
            }
            if (latestRef.current.onBlur) {
                latestRef.current.onBlur?.(val)
            }
        },
        [latestRef]
    )

    if (fieldInnerType !== 'TEXT') {
        const v = getMatchFieldValueOrErrorVariable(field, editorValue?.content?.[0]?.content)
        return (
            <VariableSelect
                width="100%"
                style={style}
                field={field}
                disabled={disabled}
                value={v}
                innerTypes={innerTypes}
                onChange={v => handleSelect(v)}
                viewOptions={viewOptions}
                formOption={formOption}
                pageOptions={pageOptions}
                upstreamOptions={upstreamOptions}
                eventOption={eventOption}
                loopOption={loopOption}
                inputOption={inputOption}
                filterOption={filterOption}
                imageOption={imageOption}
                disablePexels
                datasourceOption={datasourceOption}
                selectDataSourceProps={selectDataSourceProps}
                dataSourceList={dataSourceList}
                userOption={userOption}
                dateOption={dateOption}
                enableFormula={!effectiveDisabledFormula}
            />
        )
    }

    return (
        <SCxContainer highlighting={highlighting} style={{ ...style, cursor: disabled ? 'not-allowed' : 'default' }}>
            <NodeFieldEditorProvider editor={editor}>
                <EditorContent
                    disabled={disabled}
                    style={{ height: autoHeight && !disabled ? 'auto' : 32, cursor: 'text' }}
                    className="node-editor"
                    editor={editor}
                    placeholder={placeholder}
                />
            </NodeFieldEditorProvider>
            {!disabled && (
                <SCxExtra>
                    <VariablePicker
                        field={field?.innerType === 'TEXT' ? undefined : field}
                        innerTypes={innerTypes}
                        disabled={disabled}
                        triggerWrapper={AddVariableButton}
                        onSelect={handleEditorSelect}
                        viewOptions={viewOptions}
                        formOption={formOption}
                        pageOptions={pageOptions}
                        upstreamOptions={upstreamOptions}
                        eventOption={eventOption}
                        loopOption={loopOption}
                        inputOption={inputOption}
                        filterOption={filterOption}
                        datasourceOption={datasourceOption}
                        userOption={userOption}
                        selectDataSourceProps={selectDataSourceProps}
                        dateOption={dateOption}
                        imageOption={imageOption}
                        enableFormula={!effectiveDisabledFormula}
                        onOpenSelectDataSourceModal={handleOpenSelectDataSourceModal}
                    />
                </SCxExtra>
            )}
            {selectDataSourceProps && modalOpened && (
                <SelectDataSourceModal
                    opened={modalOpened}
                    variable={selectDataSourceVariable}
                    dataSourceList={dataSourceList}
                    {...selectDataSourceProps}
                    onClose={handleCloseSelectDataSourceModal}
                    onConfirm={handleSelect}
                />
            )}
        </SCxContainer>
    )
}
